import { useSyncExternalStore } from 'react';
import {
  applyDensity,
  applyTheme,
  isDensity,
  isThemeMode,
  type Density,
  type ThemeMode,
} from './theme.js';

// #399 — device-local preferences.
//
// These are PER DEVICE and per browser profile, not per account: a person who
// wants a dark, compact portal on their laptop and a light, roomy one on the
// wall display should get exactly that, and syncing them to the server would
// make one of those two wrong. The server-side preferences — the email
// notification toggles — live in user_preferences and are reached through
// src/api/preferences.ts instead.
//
// localStorage, which makes this the ONE file under apps/spa/src permitted to
// touch web storage. CLAUDE.md's post-deploy invariant — and the CI gate that
// enforces it — is about sign-in material: keycloak-js holds all of that in
// memory only, and nothing here goes near it. Everything this module writes is
// a short enumerated string or a small number, validated on the way in by
// coercePrefs; there is deliberately no shape in which a caller could hand it
// anything that authenticates a request.
//
// The gate — the web-storage workflow under .github/workflows/, the one whose
// name this comment cannot spell for the reason that follows — names this file
// explicitly rather than opening a pattern-shaped hole, and pairs the
// exemption with a second check: this file must not contain any of a short
// list of words for such material, ANYWHERE, comments included. That is why
// the prose above is worded the way it is, and why the workflow is not cited
// by filename: the check is a plain substring match that does not distinguish
// code from a comment, and the filename is itself one of the words. Any future
// edit that reaches in that direction, even to explain why it must not, turns
// the gate red and sends the change back through review. Read that workflow
// before rewording this paragraph.
//
// A second web-storage user has to go and edit that gate. That is the point.

export const DATE_FORMATS = ['us', 'iso', 'long'] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];

export const CLOCK_FORMATS = ['12h', '24h'] as const;
export type ClockFormat = (typeof CLOCK_FORMATS)[number];

export const CASE_FILTERS = ['all', 'mine'] as const;
export type CaseFilter = (typeof CASE_FILTERS)[number];

// The nav ids App.tsx routes to. Anything role-gated is excluded: a landing
// page an analyst cannot open would strand them on a 403 every sign-in.
export const LANDING_PAGES = [
  'dashboard', 'intake', 'onboarding', 'cases', 'notifications',
  'cure-appeals', 'revets', 'settlement', 'changes', 'ip-disputes',
] as const;
export type LandingPage = (typeof LANDING_PAGES)[number];

export const ROWS_PER_PAGE_CHOICES = [25, 50, 100, 200] as const;

export interface DevicePrefs {
  theme: ThemeMode;
  density: Density;
  rowsPerPage: number;
  /** IANA zone name, or '' for "whatever this browser is set to". */
  timezone: string;
  dateFormat: DateFormat;
  clockFormat: ClockFormat;
  landingPage: LandingPage;
  caseFilter: CaseFilter;
  /**
   * #696 — whether the pinned onboarding-data card on the KYC tab is collapsed
   * to its one-line summary. A working-style choice about screen real estate,
   * per device like the rest of this file: the analyst who works on a laptop
   * wants the line and the one at the review station wants the full card.
   */
  kycCardCollapsed: boolean;
}

// Every default reproduces the pre-#399 behaviour exactly, so an untouched
// install renders identically to before this issue: system theme is what the
// old frozen light palette effectively was for a light-mode user, comfortable
// density is the old padding, 50 rows is useCaseQuery's old literal, an empty
// timezone is the browser's, and 'us' + '12h' are what toLocaleString was
// already producing.
export const DEFAULT_PREFS: DevicePrefs = {
  theme: 'system',
  density: 'comfortable',
  rowsPerPage: 50,
  timezone: '',
  dateFormat: 'us',
  clockFormat: '12h',
  landingPage: 'dashboard',
  caseFilter: 'all',
  // Expanded, which is how the card rendered before #696 pinned it.
  kycCardCollapsed: false,
};

const STORAGE_KEY = 'bcid.prefs.v1';

const isOneOf = <T extends string>(allowed: readonly T[], v: unknown): v is T =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v);

/**
 * Validate field by field, falling back to the default per field rather than
 * discarding the whole object. Stored preferences outlive the code that wrote
 * them: a build that renames one enum value must not cost the user the other
 * six settings.
 *
 * The timezone is checked against Intl rather than a list — the zone database
 * is the browser's, and hardcoding a copy of it here would go stale. A zone
 * this browser has never heard of would throw on every timestamp, so it is
 * rejected at the boundary instead.
 */
export function coercePrefs(raw: unknown): DevicePrefs {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_PREFS };
  const r = raw as Record<string, unknown>;
  return {
    theme: isThemeMode(r.theme) ? r.theme : DEFAULT_PREFS.theme,
    density: isDensity(r.density) ? r.density : DEFAULT_PREFS.density,
    rowsPerPage: (ROWS_PER_PAGE_CHOICES as readonly number[]).includes(r.rowsPerPage as number)
      ? (r.rowsPerPage as number)
      : DEFAULT_PREFS.rowsPerPage,
    timezone: isSupportedTimezone(r.timezone) ? (r.timezone as string) : DEFAULT_PREFS.timezone,
    dateFormat: isOneOf(DATE_FORMATS, r.dateFormat) ? r.dateFormat : DEFAULT_PREFS.dateFormat,
    clockFormat: isOneOf(CLOCK_FORMATS, r.clockFormat) ? r.clockFormat : DEFAULT_PREFS.clockFormat,
    landingPage: isOneOf(LANDING_PAGES, r.landingPage) ? r.landingPage : DEFAULT_PREFS.landingPage,
    caseFilter: isOneOf(CASE_FILTERS, r.caseFilter) ? r.caseFilter : DEFAULT_PREFS.caseFilter,
    kycCardCollapsed: typeof r.kycCardCollapsed === 'boolean'
      ? r.kycCardCollapsed
      : DEFAULT_PREFS.kycCardCollapsed,
  };
}

export function isSupportedTimezone(tz: unknown): boolean {
  if (tz === '') return true; // "browser default", the documented empty case
  if (typeof tz !== 'string' || tz.length === 0) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function read(): DevicePrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return coercePrefs(raw ? JSON.parse(raw) : null);
  } catch {
    // Private mode, disabled site data, or a corrupt value. Defaults are a
    // working portal; throwing here would be a blank screen.
    return { ...DEFAULT_PREFS };
  }
}

let current: DevicePrefs = read();
const listeners = new Set<() => void>();

/**
 * The current preferences. A plain read, safe to call from non-React code
 * (util.ts's formatters and useCaseQuery's default page size both do).
 */
export function getPrefs(): DevicePrefs {
  return current;
}

export function setPrefs(patch: Partial<DevicePrefs>): DevicePrefs {
  current = coercePrefs({ ...current, ...patch });
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    // The setting still applies for this session; only persistence is lost.
    // Silently degrading beats refusing to change the theme.
  }
  applyDocumentPrefs(current);
  for (const l of listeners) l();
  return current;
}

export function resetPrefs(): DevicePrefs {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* see setPrefs */ }
  current = { ...DEFAULT_PREFS };
  applyDocumentPrefs(current);
  for (const l of listeners) l();
  return current;
}

/** Test seam: drop the in-memory copy and re-read storage. */
export function reloadPrefs(): DevicePrefs {
  current = read();
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Re-renders the calling component whenever any preference changes. */
export function usePrefs(): DevicePrefs {
  return useSyncExternalStore(subscribe, getPrefs, getPrefs);
}

/** Stamp the two document-level attributes global.css keys off. */
export function applyDocumentPrefs(prefs: DevicePrefs = current): void {
  if (typeof document === 'undefined') return;
  applyTheme(prefs.theme);
  applyDensity(prefs.density);
}

// Applied at MODULE EVALUATION rather than from a React effect: this is the
// earliest point in the BUNDLE's life, so an explicit light/dark choice is
// stamped before React renders anything instead of after a first paint in the
// wrong palette. global.css carries "system" with no JavaScript at all, so
// that case never flashes.
//
// It is not the earliest point in the PAGE's life. A render-blocking
// <script src> in <head> would beat it, and would also close the remaining
// flash for a user whose explicit choice disagrees with their OS — that option
// is open to us (`script-src 'self'` permits a same-origin external script;
// only an inline one is blocked). It is deliberately not taken: a
// render-blocking request on every page load is a poor trade for a sub-paint
// flash affecting only the minority who overrode their OS setting.
applyDocumentPrefs(current);
